import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { useAuditTypes } from '@/app/(client)/audits/-hooks/useAuditsQuery';
import { PageContainer } from '@/components/shared/PageContainer';
import {
  PageHeaderCard,
  PageHeaderIdentity,
  PageHeaderTopRow,
} from '@/components/shared/PageHeaderCard';
import { Badge } from '@/components/ui/badge';
import {
  useCollaborationStatusOptions,
  useFacilityProcessOptions,
  useNetworkCompanyOptions,
} from '@/hooks/useDropdowns';
import { useTranslator } from '@/hooks/useTranslator';
import { COUNTRY_OPTIONS } from '@/lib/constants/countryOptions';
import { useSessionStore } from '@/store/sessionStore';
import { AuditDrilldownTable } from '../../../../../-components/audit-compliance/AuditDrilldownTable';
import {
  buildAuditDrilldownChips,
  buildAuditDrilldownFilter,
  parseFilterParam,
} from '../../../../../-utils/drilldownUtils';

export const Route = createFileRoute(
  '/(client)/report/(pages)/(drilldown)/audit-compliance/drilldown/$chartType/',
)({
  validateSearch: z.object({
    filter: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
  }),
  component: AuditDrilldownPage,
});

function AuditDrilldownPage() {
  const { getTranslation: t } = useTranslator({ page: 'auditCompliance' });
  const { chartType } = useParams({ strict: false }) as { chartType: string };
  const search = (useSearch({ strict: false }) ?? {}) as {
    filter?: string | Record<string, unknown>;
  };
  const companyId = useSessionStore(s => s.user?.companyId ?? '');

  const filterObj = parseFilterParam(search.filter);
  const drilldownFilter = buildAuditDrilldownFilter(filterObj);

  // Load options to resolve IDs → display labels (matches old frontend behaviour)
  const { data: supplierOptions = [] } = useNetworkCompanyOptions();
  const { data: collabStatusOptions = [] } = useCollaborationStatusOptions();
  const { data: processOptions = [] } = useFacilityProcessOptions();
  const countryOptions = COUNTRY_OPTIONS;
  const { data: auditTypesData } = useAuditTypes();
  const auditTypeOptions = (auditTypesData?.data ?? []).map(at => ({
    key: at.id,
    label: at.name,
  }));

  const filterChips = buildAuditDrilldownChips({
    filter: drilldownFilter,
    t,
    supplierOptions,
    collabStatusOptions,
    processOptions,
    countryOptions: countryOptions as { key: string; label: string }[],
    auditTypeOptions,
  });
  const chartTitle = t(`drilldown.chartType.${chartType}`) || chartType;

  return (
    <PageContainer className='flex flex-col gap-4 overflow-hidden'>
      <PageHeaderCard className='shrink-0'>
        <PageHeaderTopRow>
          <PageHeaderIdentity
            title={`${t('drilldown.headerPrefix')}${chartTitle}`}
            subtitle={
              <>
                <span className='block'>{t('drilldown.description')}</span>
                <span className='block'>{t('drilldown.filterNote')}</span>
              </>
            }
            badges={
              filterChips.length > 0
                ? filterChips.map(chip => (
                    <Badge
                      key={chip}
                      variant='secondary'
                      className='rounded-md text-xs font-normal'
                    >
                      {chip}
                    </Badge>
                  ))
                : undefined
            }
          />
        </PageHeaderTopRow>
      </PageHeaderCard>
      <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
        <AuditDrilldownTable companyId={companyId} filter={drilldownFilter} />
      </div>
    </PageContainer>
  );
}
